// src/pages/MainPage.tsx

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Container, Button, Input } from '../styles/GlobalStyles';
import { getSurgeriesByInstitution, getPatientsByInstitution, getDoctorsByInstitution } from '../services/firebase';
import { Surgery, Patient, Doctor } from '../types';
import DoctorFormModal from '../components/DoctorFormModal';
import SurgeryFormModal from '../components/SurgeryFormModal';
import PatientFormModal from '../components/PatientFormModal';
import SurgeryDetailModal from '../components/SurgeryDetailModal';
import { useTranslation } from '../i18n';
import { useFormatDate } from '../hooks/useFormatDate';

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 40px;
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: #1f2937;
`;

const TitleContainer = styled.div``;

const LogoImage = styled.img`
  height: 30px;
  margin-right: 8px;
`;

const Subtitle = styled.div`
  font-size: 14px;
  color: #6b7280;
`;

const InstitutionInfo = styled.div`
  background-color: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 8px;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const InstitutionText = styled.div`
  font-size: 16px;
  color: #0369a1;
  font-weight: 600;
`;

const ChangeInstitutionButton = styled.button`
  background: none;
  border: 1px solid #0369a1;
  color: #0369a1;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: #0369a1;
    color: white;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 12px;
`;

const SearchBar = styled.div`
  position: relative;
`;

const SearchInput = styled(Input)`
  padding-right: 48px;
`;

const SearchIcon = styled.div`
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #6b7280;
`;

const ContentTabs = styled.div`
  display: flex;
  margin-bottom: 32px;
  border-bottom: 1px solid #e5e7eb;
`;

const Tab = styled.button<{ isActive: boolean }>`
  padding: 12px 24px;
  border: none;
  background: none;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  color: ${props => props.isActive ? '#0369a1' : '#6b7280'};
  border-bottom: 2px solid ${props => props.isActive ? '#0369a1' : 'transparent'};
  transition: all 0.2s ease;
  
  &:hover {
    color: #0369a1;
  }
`;

const SurgeryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
  margin-bottom: 40px;
`;

const SurgeryCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
`;

const SurgeryName = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 12px;
`;

const SurgeryInfo = styled.div`
  font-size: 14px;
  color: #6b7280;
`;

const PatientCount = styled.span`
  display: inline-block;
  background-color: #ddd6fe;
  color: #7c3aed;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
`;

const PersonGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
  margin-bottom: 40px;
`;

const PersonCard = styled.div<{ clickable?: boolean }>`
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  transition: all 0.2s ease;
  ${props => props.clickable && `
    cursor: pointer;
    &:hover {
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
      transform: translateY(-2px);
    }
  `}
  
  &:hover {
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
`;

const PersonHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const PersonName = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
`;

const PersonBadge = styled.span<{ type: 'patient' | 'doctor' }>`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  
  ${props => props.type === 'doctor' 
    ? `
      background-color: #dcfce7;
      color: #166534;
    `
    : `
      background-color: #dbeafe;
      color: #1e40af;
    `
  }
`;

const PersonInfo = styled.div`
  font-size: 14px;
  color: #6b7280;
  line-height: 1.5;
`;

const PersonSignificant = styled.div`
  margin-top: 8px;
  padding: 8px 12px;
  background-color: #f8fafc;
  border-radius: 6px;
  font-size: 13px;
  color: #475569;
  border-left: 3px solid #e2e8f0;
`;

const SurgeryInfoItem = styled.div`
  margin-bottom: 8px;
  
  &:last-of-type {
    margin-bottom: 12px;
  }
`;

const PersonTypeLabel = styled.div`
  margin-bottom: 8px;
`;

const PersonAgeGender = styled.div``;

const PersonSignificantLabel = styled.span`
  font-weight: 500;
`;

const EmptyStateContainer = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #6b7280;
`;

const EmptyStateTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 8px;
  color: #374151;
`;

const EmptyStateDescription = styled.p``;

const LoadingContainer = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #6b7280;
`;

// Pagination styles
const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin-top: 32px;
  padding: 20px 0;
`;

const PaginationButton = styled.button<{ isActive?: boolean; disabled?: boolean }>`
  padding: 8px 12px;
  border: 1px solid #e5e7eb;
  background: ${props => props.isActive ? '#0369a1' : 'white'};
  color: ${props => props.isActive ? 'white' : '#374151'};
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.disabled ? 0.5 : 1};
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background: ${props => props.isActive ? '#5b59f0' : '#f9fafb'};
    border-color: ${props => props.isActive ? '#5b59f0' : '#d1d5db'};
  }
  
  &:disabled {
    cursor: not-allowed;
  }
`;

const PaginationInfo = styled.div`
  font-size: 14px;
  color: #6b7280;
  margin: 0 16px;
`;

const ContentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const ItemsPerPageSelect = styled.select`
  padding: 6px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 14px;
  background: white;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: #0369a1;
    box-shadow: 0 0 0 3px rgba(3, 105, 161, 0.1);
  }
`;

const ITEMS_PER_PAGE_OPTIONS = [6, 12, 24, 48];

const MainPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { formatDate } = useFormatDate();
  
  const institutionNumber = sessionStorage.getItem('institutionNumber') || '';
  
  const [surgeries, setSurgeries] = useState<Surgery[]>([]);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [doctors, setDoctors] = useState<Doctor[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState<'surgeries' | 'patients' | 'doctors'>('surgeries');
  const [isDoctorModalOpen, setIsDoctorModalOpen] = useState(false);
  const [selectedDoctor, setSelectedDoctor] = useState<Doctor | undefined>(undefined);
  const [isSurgeryModalOpen, setIsSurgeryModalOpen] = useState(false);
  const [isPatientModalOpen, setIsPatientModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedSurgery, setSelectedSurgery] = useState<Surgery | null>(null);
  const [loading, setLoading] = useState(true);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(12);

  useEffect(() => {
    if (!institutionNumber) {
      navigate('/');
    }
  }, [institutionNumber, navigate]);

  useEffect(() => {
    if (institutionNumber) {
      loadData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [institutionNumber]);

  // Reset to first page when search term or tab changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, activeTab]);

  const loadData = async () => {
    if (!institutionNumber) return;
    setLoading(true);
    try {
      const [surgeriesData, patientsData, doctorsData] = await Promise.all([
        getSurgeriesByInstitution(institutionNumber),
        getPatientsByInstitution(institutionNumber),
        getDoctorsByInstitution(institutionNumber)
      ]);
      setSurgeries(surgeriesData);
      setPatients(patientsData);
      setDoctors(doctorsData);
    } catch (error) {
      console.error('데이터 로드 오류:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredSurgeries = surgeries.filter(surgery => surgery.name.toLowerCase().includes(searchTerm.toLowerCase()));
  const filteredPatients = patients.filter(patient => patient.name.toLowerCase().includes(searchTerm.toLowerCase()));
  const filteredDoctors = doctors.filter(doctor => doctor.name.toLowerCase().includes(searchTerm.toLowerCase()) || doctor.doctorNumber.includes(searchTerm));

  // Get current data based on active tab
  const getCurrentData = () => {
    switch (activeTab) {
      case 'surgeries': return filteredSurgeries;
      case 'patients': return filteredPatients;
      case 'doctors': return filteredDoctors;
      default: return [];
    }
  };

  const currentData = getCurrentData();
  const totalItems = currentData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = currentData.slice(startIndex, endIndex);

  const handleSurgeryClick = (surgery: Surgery) => {
    setSelectedSurgery(surgery);
    setIsDetailModalOpen(true);
  };

  const handleDoctorClick = (doctor: Doctor) => {
    setSelectedDoctor(doctor);
    setIsDoctorModalOpen(true);
  };

  const handleChangeInstitution = () => {
    sessionStorage.removeItem('institutionNumber');
    navigate('/');
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const getTabPlaceholder = () => {
    switch (activeTab) {
      case 'surgeries': return t('dashboard.search.surgeries');
      case 'patients': return t('dashboard.search.patients');
      case 'doctors': return t('dashboard.search.doctors');
      default: return t('common.searchPlaceholder');
    }
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pageNumbers = [];
    const maxVisiblePages = 5;
    
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return (
      <PaginationContainer>
        <PaginationButton
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          {t('pagination.previous')}
        </PaginationButton>
        
        {startPage > 1 && (
          <>
            <PaginationButton onClick={() => handlePageChange(1)}>1</PaginationButton>
            {startPage > 2 && <span>...</span>}
          </>
        )}
        
        {pageNumbers.map(page => (
          <PaginationButton
            key={page}
            onClick={() => handlePageChange(page)}
            isActive={page === currentPage}
          >
            {page}
          </PaginationButton>
        ))}
        
        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && <span>...</span>}
            <PaginationButton onClick={() => handlePageChange(totalPages)}>{totalPages}</PaginationButton>
          </>
        )}
        
        <PaginationButton
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          {t('pagination.next')}
        </PaginationButton>
        
        <PaginationInfo>
          {t('pagination.info', { 
            start: startIndex + 1, 
            end: Math.min(endIndex, totalItems), 
            total: totalItems 
          })}
        </PaginationInfo>
      </PaginationContainer>
    );
  };

  const renderEmptyState = (typeKey: 'surgeries' | 'patients' | 'doctors') => (
    <EmptyStateContainer>
      {searchTerm ? (
        <>
          <EmptyStateTitle>{t('dashboard.empty.search_title', { searchTerm })}</EmptyStateTitle>
          <EmptyStateDescription>{t('dashboard.empty.search_desc')}</EmptyStateDescription>
        </>
      ) : (
        <>
          <EmptyStateTitle>{t(`dashboard.empty.${typeKey}_title`)}</EmptyStateTitle>
          <EmptyStateDescription>{t(`dashboard.empty.${typeKey}_desc`)}</EmptyStateDescription>
        </>
      )}
    </EmptyStateContainer>
  );

  const renderContent = () => {
    if (currentItems.length === 0) {
      return renderEmptyState(activeTab);
    }

    switch (activeTab) {
      case 'surgeries':
        return (
          <>
            <SurgeryGrid>
              {(currentItems as Surgery[]).map(surgery => (
                <SurgeryCard key={surgery.id} onClick={() => handleSurgeryClick(surgery)}>
                  <SurgeryName>{surgery.name}</SurgeryName>
                  <SurgeryInfo>
                    <SurgeryInfoItem>{t('dashboard.surgeryCard.sections')}: {surgery.sections.length}</SurgeryInfoItem>
                    <SurgeryInfoItem>{t('dashboard.surgeryCard.createdAt')}: {formatDate(surgery.createdAt)}</SurgeryInfoItem>
                    <PatientCount>{t('dashboard.surgeryCard.patientCount', { count: surgery.patientIds.length })}</PatientCount>
                  </SurgeryInfo>
                </SurgeryCard>
              ))}
            </SurgeryGrid>
            {renderPagination()}
          </>
        );
      case 'patients':
        return (
          <>
            <PersonGrid>
              {(currentItems as Patient[]).map(patient => (
                <PersonCard key={patient.id}>
                  <PersonHeader>
                    <PersonName>{patient.name}</PersonName>
                    <PersonBadge type="patient">{t('dashboard.tabs.patients')}</PersonBadge>
                  </PersonHeader>
                  <PersonInfo>
                    <PersonTypeLabel>{t('dashboard.patientCard.nickname')}: {patient.name}</PersonTypeLabel>
                  </PersonInfo>
                </PersonCard>
              ))}
            </PersonGrid>
            {renderPagination()}
          </>
        );
      case 'doctors':
        return (
          <>
            <PersonGrid>
              {(currentItems as Doctor[]).map(doctor => (
                <PersonCard key={doctor.id} clickable onClick={() => handleDoctorClick(doctor)}>
                  <PersonHeader>
                    <PersonName>{doctor.name}</PersonName>
                    <PersonBadge type="doctor">{t('dashboard.tabs.doctors')}</PersonBadge>
                  </PersonHeader>
                  <PersonInfo>
                    <PersonTypeLabel>{t('dashboard.doctorCard.doctorNumber')}: {doctor.doctorNumber}</PersonTypeLabel>
                    <PersonAgeGender>{t('dashboard.doctorCard.ageGender', { age: doctor.age, gender: t(`gender.${doctor.gender === '남성' ? 'male' : 'female'}`) })}</PersonAgeGender>
                  </PersonInfo>
                  {doctor.significant && (
                    <PersonSignificant>
                      <PersonSignificantLabel>{t('dashboard.doctorCard.significant')}: </PersonSignificantLabel>
                      {doctor.significant}
                    </PersonSignificant>
                  )}
                </PersonCard>
              ))}
            </PersonGrid>
            {renderPagination()}
          </>
        );
      default:
        return null;
    }
  };

  if (!institutionNumber) return null;
  if (loading) return <Container><LoadingContainer>{t('dashboard.loading')}</LoadingContainer></Container>;

  return (
    <Container>
      <Header>
        <TitleContainer>
          <Title><LogoImage src="/osler_logo.png" alt="Osler" />{t('dashboard.title')}</Title>
          <Subtitle>{t('dashboard.subtitle')}</Subtitle>
        </TitleContainer>
        <InstitutionInfo>
          <InstitutionText>{t('dashboard.institutionLabel')}: {institutionNumber}</InstitutionText>
          <ChangeInstitutionButton onClick={handleChangeInstitution}>{t('dashboard.changeInstitution')}</ChangeInstitutionButton>
        </InstitutionInfo>
        <ActionButtons>
          <Button variant="primary" onClick={() => { setSelectedDoctor(undefined); setIsDoctorModalOpen(true); }}>{t('dashboard.addDoctor')}</Button>
          <Button variant="primary" onClick={() => setIsPatientModalOpen(true)}>{t('dashboard.addPatient')}</Button>
          <Button variant="success" onClick={() => setIsSurgeryModalOpen(true)}>{t('dashboard.addSurgery')}</Button>
        </ActionButtons>
      </Header>

      <ContentTabs>
        <Tab isActive={activeTab === 'surgeries'} onClick={() => setActiveTab('surgeries')}>
          {t('dashboard.tabs.surgeries')} ({surgeries.length})
        </Tab>
        <Tab isActive={activeTab === 'patients'} onClick={() => setActiveTab('patients')}>
          {t('dashboard.tabs.patients')} ({patients.length})
        </Tab>
        <Tab isActive={activeTab === 'doctors'} onClick={() => setActiveTab('doctors')}>
          {t('dashboard.tabs.doctors')} ({doctors.length})
        </Tab>
      </ContentTabs>

      <ContentHeader>
        <SearchBar>
          <SearchInput type="text" placeholder={getTabPlaceholder()} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
        </SearchBar>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <label htmlFor="itemsPerPage" style={{ fontSize: '14px', color: '#6b7280' }}>
            {t('pagination.itemsPerPage')}:
          </label>
          <ItemsPerPageSelect
            id="itemsPerPage"
            value={itemsPerPage}
            onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
          >
            {ITEMS_PER_PAGE_OPTIONS.map(option => (
              <option key={option} value={option}>{option}</option>
            ))}
          </ItemsPerPageSelect>
        </div>
      </ContentHeader>

      {renderContent()}

      <DoctorFormModal 
        isOpen={isDoctorModalOpen} 
        onClose={() => { setIsDoctorModalOpen(false); setSelectedDoctor(undefined); }} 
        onDoctorAdded={loadData} 
        doctor={selectedDoctor}
      />
      <SurgeryFormModal isOpen={isSurgeryModalOpen} onClose={() => setIsSurgeryModalOpen(false)} onSurgeryAdded={loadData} />
      <PatientFormModal isOpen={isPatientModalOpen} onClose={() => setIsPatientModalOpen(false)} onPatientAdded={loadData} />
      {selectedSurgery && <SurgeryDetailModal isOpen={isDetailModalOpen} onClose={() => { setIsDetailModalOpen(false); setSelectedSurgery(null); }} surgery={selectedSurgery} patients={patients} doctors={doctors} onSurgeryUpdated={loadData} />}
    </Container>
  );
};

export default MainPage;